import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Link
} from '@mui/material';
import { useI18n } from '../i18n';
import authService from '../services/authService';
import { environment } from '../config/environment';
import { getApiHeaders } from '../utils/apiUtils';
import '../styles/login.css';

interface OTPVerificationProps {
  email: string;
  onVerify: (otp: string) => void;
  onResend: () => void;
  onChangeEmail: () => void;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({
  email,
  onVerify,
  onResend,
  onChangeEmail
}) => {
  const { t } = useI18n();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [testingOtpCode, setTestingOtpCode] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);

  // Detectar cambios en el ancho de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determinar si es móvil (ancho < 1000px)
  const isMobile = windowWidth < 1000;

  // Verificar si está en modo testing
  const isTestingMode = authService.isTestingMode();

  // Obtener el código OTP del localStorage cuando está en modo testing
  useEffect(() => {
    if (isTestingMode) {
      try {
        const storedOtp = localStorage.getItem('petflix_testing_otp');
        const storedEmail = localStorage.getItem('petflix_testing_otp_email');
        // Solo mostrar el OTP si el email coincide
        if (storedOtp && storedEmail === email) {
          setTestingOtpCode(storedOtp);
        }
      } catch (e) {
        console.warn('No se pudo leer OTP del localStorage:', e);
      }
    }
  }, [email, isTestingMode]);

  const handleInputChange = (index: number, value: string) => {
    // Solo permitir números
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus al siguiente campo si se ingresó un dígito
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Retroceder al campo anterior con Backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
    setOtp(newOtp);
    
    // Focus al último campo llenado
    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleVerify = async () => {
    const otpString = otp.join('');
    if (otpString.length === 6) {
      try {
        // Detectar si es email (contiene @) o teléfono
        const isEmail = email.includes('@');
        let result;
        
        if (isEmail) {
          result = await authService.verifyOTP(email, otpString);
        } else {
          // Es un teléfono
          result = await authService.verifyPhoneOTP(email, otpString);
        }
        
        if (result.success) {
          // Guardar datos de geolocalización después de verificar el OTP exitosamente (solo para email)
          if (isEmail) {
            try {
              const geoResponse = await fetch(`${environment.apiBaseUrl}/geoip/save`, {
                method: 'POST',
                headers: getApiHeaders(),
                body: JSON.stringify({
                  user_email: email
                })
              });

              if (geoResponse.ok) {
                const geoData = await geoResponse.json();
                console.log('✅ Datos de geolocalización guardados:', geoData);
              } else {
                console.warn('⚠️ No se pudieron guardar los datos de geolocalización, pero el login continúa');
              }
            } catch (geoError) {
              // No bloquear el login si falla el guardado de geolocalización
              console.warn('⚠️ Error guardando datos de geolocalización:', geoError);
            }
          }

          onVerify(otpString);
        } else {
          console.error('OTP verification failed:', result.error);
          // Aquí podrías mostrar un error al usuario
        }
      } catch (error) {
        console.error('OTP verification error:', error);
        // Aquí podrías mostrar un error al usuario
      }
    }
  };

  const isOtpComplete = otp.every(digit => digit !== '');

  return (
    <Container maxWidth="lg" className="login-container" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
      <Paper
        elevation={8}
        className="login-card"
        sx={{
          width: '100%',
          maxWidth: isMobile ? '100%' : 1000,
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          minHeight: isMobile ? 'auto' : 600,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}
      >
        {/* Panel Izquierdo - Formulario de Verificación OTP */}
        <Box
          className="login-form"
          sx={{
            flex: 1,
            backgroundColor: 'white',
            p: isMobile ? 3 : 6,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: isMobile ? '100%' : 'auto',
            minWidth: isMobile ? '100%' : 'auto',
            order: isMobile ? 2 : 0
          }}
        >
          {/* Logo y Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box className="logo-container" aria-hidden="true">
              <Box
                component="svg"
                viewBox="0 0 64 64"
                sx={{ width: 38, height: 38, color: 'white' }}
              >
                <path
                  fill="currentColor"
                  d="M17.6 29.7c4.2-2.6 8.7-3.9 14.4-3.9s10.2 1.3 14.4 3.9c4.7 2.9 7.2 7.4 6.3 11.4-1.1 5.1-6.5 7.4-12 5.2-3.2-1.3-5.5-2-8.7-2s-5.5.7-8.7 2c-5.5 2.2-10.9-.1-12-5.2-.9-4 1.6-8.5 6.3-11.4Zm-5-2.5c-3.5 0-6.3-3.5-6.3-7.8s2.8-7.8 6.3-7.8 6.3 3.5 6.3 7.8-2.8 7.8-6.3 7.8Zm12.2-5.9c-3.5 0-6.3-3.7-6.3-8.2s2.8-8.2 6.3-8.2 6.3 3.7 6.3 8.2-2.8 8.2-6.3 8.2Zm14.4 0c-3.5 0-6.3-3.7-6.3-8.2s2.8-8.2 6.3-8.2 6.3 3.7 6.3 8.2-2.8 8.2-6.3 8.2Zm12.2 5.9c-3.5 0-6.3-3.5-6.3-7.8s2.8-7.8 6.3-7.8 6.3 3.5 6.3 7.8-2.8 7.8-6.3 7.8Z"
                />
              </Box>
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                color: '#0E0F12',
                mb: 1,
                fontSize: '28px'
              }}
            >
              Noah's Ark Pet Connect
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#6B6F7B',
                fontSize: '14px',
                mb: 2
              }}
            >
              Pet Contest Ecosystem
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#0E0F12',
                fontSize: '24px'
              }}
            >
              One ark. One feeling.
            </Typography>
          </Box>

          {/* Email Display */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="body1"
              sx={{
                color: '#6B6F7B',
                fontSize: '16px'
              }}
            >
              {email}
            </Typography>
            {isTestingMode && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#F26B42',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'block',
                    mb: 1
                  }}
                >
                  MODO TESTING - Revisa la consola para el código OTP
                </Typography>
                {testingOtpCode && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#0E0F12',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      fontFamily: 'monospace',
                      backgroundColor: '#FFF8E8',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      display: 'inline-block',
                      letterSpacing: '4px',
                      border: '2px solid #ff9800'
                    }}
                  >
                    {testingOtpCode}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* OTP Input Fields */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                mb: 3,
                color: '#0E0F12',
                fontWeight: 'bold'
              }}
            >
              Enter Verification Code
            </Typography>
            
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: isMobile ? 1 : 2,
                mb: 3,
                flexWrap: 'wrap'
              }}
            >
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: 'center',
                      fontSize: isMobile ? '20px' : '24px',
                      fontWeight: 'bold'
                    }
                  }}
                  sx={{
                    width: isMobile ? 40 : 50,
                    height: isMobile ? 50 : 60,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      height: isMobile ? 50 : 60,
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                        borderWidth: 2
                      },
                      '&:hover fieldset': {
                        borderColor: '#ED1790'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ED1790',
                        borderWidth: 2
                      }
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Verify Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerify}
            disabled={!isOtpComplete}
            className="signin-button"
            sx={{
              mb: 3,
              opacity: isOtpComplete ? 1 : 0.6,
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999'
              }
            }}
          >
            VERIFY OTP
          </Button>

          {/* Action Links */}
          <Box sx={{ 
            textAlign: 'center', 
            mb: 4,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            gap: isMobile ? 1 : 0
          }}>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onResend();
              }}
              sx={{
                color: '#B01F8E',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 'bold',
                mr: isMobile ? 0 : 3,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              RESEND OTP
            </Link>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onChangeEmail();
              }}
              sx={{
                color: '#306FC8',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 'bold',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Change Email
            </Link>
          </Box>

          {/* Enlaces del Footer */}
          <Box className="footer-links">
            <Link href="#" className="footer-link">
              Privacy policy
            </Link>
            <Link href="#" className="footer-link">
              Terms & conditions
            </Link>
          </Box>
        </Box>

        {/* Panel Derecho - Imagen Decorativa */}
        <Box 
          className="login-image" 
          sx={{ 
            flex: 1,
            minHeight: isMobile ? '400px' : 'auto',
            display: isMobile ? 'block' : 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: isMobile ? 'flex-start' : 'center',
            order: isMobile ? 1 : 0
          }}
        >
          {/* Contenido decorativo */}
          <Box className="login-image-content">
            <Typography
              variant={isMobile ? "h4" : "h3"}
              sx={{
                fontWeight: 'bold',
                mb: 2,
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}
            >
              Verify your place in the ark.
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              sx={{
                opacity: 0.9,
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Secure access, soft steps, and one shared home for every contest.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default OTPVerification;
